import { Head } from '@unhead/react';
import React from 'react';

import {
    AjaxListingContext,
    AjaxListingNextButton
} from '$partials/base';

import {
    Footer,
    Header
} from '$partials/global';

import { ArticleListingItem } from '$partials/items';
import { ModuleDispatcher } from '$partials/modules';
import { useAjaxListing } from '$hooks/ajax-listing';
import { useMultiSelectFilter } from '$hooks/filters';
import ArticleSplitColumns from '$partials/modules/blocks/article-split-columns';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').ArticleListingPage}
 */
const ArticleListingPage = (props) => {
    const page = props.data.page;

    const articleTypeDetailPageFilter = useMultiSelectFilter(page?.filters?.articleTypeDetailPage);

    const listing = useAjaxListing({
        infinite: false,
        items: page?.items?.edges,
        page: page?.items?.pageInfo?.page,
        path: `/api/v1/listings${page?.url ?? ''}`,
        totalItems: page?.items?.total,
        totalPages: page?.items?.pageInfo?.totalPages,
        query: {
            articleType: articleTypeDetailPageFilter.value || undefined
        }
    });

    const filterView = Boolean(articleTypeDetailPageFilter.value.length);

    return (
        <>
            <Head>
                <link
                    data-next-head=''
                    href={`${props.domain}/rss${page.url}.xml`}
                    rel='alternate'
                    title={`Business Leader: ${page.title}`}
                    type='application/rss+xml'>
                </link>
            </Head>

            <Header
                {...props?.data?.header}
                pageType={page.__typename}/>

            <main id='main-content'>
                <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                    <div className='col-wrapper-breakout bg-surface-panel-red rounded-lg'>
                        <div className='py-24'>
                            {/* Header & Tag Cloud */}
                            <section className='col-padding grid md:grid-cols-2 gap-x-16'>
                                <h1 className='type-style-sans-display pb-16'>
                                    {page.title}
                                </h1>

                                {articleTypeDetailPageFilter?.options?.length > 0 && (
                                    <fieldset className='flex flex-wrap gap-4 w-8/12 md:w-full'>
                                        <legend className='sr-only w-full type-style-sans-body pb-8'>
                                            Article Type Filters
                                        </legend>

                                        {articleTypeDetailPageFilter.options.map((opt, idx) => {
                                            return (
                                                <div key={idx}>
                                                    <input
                                                        checked={articleTypeDetailPageFilter.value?.includes(opt.value) || false}
                                                        className='sr-only peer'
                                                        id={`frm-articleType-${opt.value}`}
                                                        name='articleType'
                                                        onChange={(e) => articleTypeDetailPageFilter.toggleValue(e.target.value)}
                                                        type='checkbox'
                                                        value={opt.value} />

                                                    <label
                                                        className='button-outcome'
                                                        htmlFor={`frm-articleType-${opt.value}`}>
                                                        <span>{opt.label}</span>
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </fieldset>
                                )}
                            </section>

                            {!filterView && (
                                <>
                                    {/* Latest articles */}
                                    <div className='mt-56 col-padding'>
                                        <h2 className='type-style-sans-h2-med'>
                                            {page.latestTitle || 'Latest'}
                                        </h2>

                                        {page.latestText && (
                                            <p className='type-style-sans-h3 mt-4 w-full sm:w-7/12 md:w-5/12'>
                                                {page.latestText}
                                            </p>
                                        )}

                                        {page.latestItems.length > 0 && (
                                            <div className='mt-16'>
                                                {/* Hero article */}
                                                <ArticleListingItem
                                                    orientation='wide'
                                                    {...page.latestItems[0]}/>

                                                {/* Split column articles */}
                                                {page.latestItems.length > 1 && (
                                                    <div className='mt-16'>
                                                        <ArticleSplitColumns items={page.latestItems.slice(1)}/>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Modules */}
                                    <div className='flex flex-col mt-56 gap-y-56'>
                                        {page.modules.map((module, i) => (
                                            <ModuleDispatcher
                                                {...module}
                                                key={i + module?.id} />
                                        ))}
                                    </div>
                                </>
                            )}

                            {filterView && (
                                <div className='mt-56 col-padding flex flex-col'>
                                    <p className='type-style-sans-h3'>
                                        {listing.totalItems} Results
                                    </p>

                                    <AjaxListingContext.Provider value={listing}>
                                        <div className='grid sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-16 mt-8 md:mt-16'>
                                            {listing?.items?.map((itm, idx) => {
                                                return (
                                                    <article
                                                        aria-posinset={idx + 1}
                                                        aria-setsize={listing.items.length}
                                                        className=''
                                                        key={itm.node.id + '-' + idx}>
                                                        <ArticleListingItem {...itm.node} />
                                                    </article>
                                                );
                                            })}
                                        </div>

                                        {(listing?.items?.length > 0) ? (
                                            listing?.page !== listing?.totalPages && (
                                                <AjaxListingNextButton
                                                    aria-label='Load more articles'
                                                    className='button-outcome mt-56 self-start'>
                                                    <span>Load more...</span>
                                                </AjaxListingNextButton>
                                            )
                                        ) : (
                                            <p className='type-style-sans-body'>
                                                No articles

                                                <span className='sr-only'>
                                                Found
                                                </span>
                                            </p>
                                        )}
                                    </AjaxListingContext.Provider>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <Footer
                {...props?.data?.footer}
                pageType={page.__typename}/>
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default ArticleListingPage;
